import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import {
    FACTORY_CONFIG_ID,
    handleResponse,
    liveHeaderHeight,
    RATING_TYPES,
    updateAssetTreeState
} from '../common/utils'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import { UserTable } from './operatorTable'
import { LineProductSelect } from './lineProductsSelect'
import { postOrEditLine } from '../common/apis'
import { configsContext } from './AssetConfigs'
import { getLineConfigSaveDisabledStatus, getLineRequestParams } from './utils'

import LiveTooltip from 'live/components/ui/tooltip'
import { DeviationsTable } from '../common/deviationsTable'
type IProps = QueryParams

export const emptyOperatorData = (): LineOperators => ({ userData: null, operatorType: null })
export function LineConfig({ line, country, factory }: IProps): JSX.Element {
    const { setAssetTree, assetType, assetTree } = useContext(configsContext)
    const [isLoading, setIsLoading] = useState(false)
    const [lineName, setLineName] = useState(line)
    const [technology, setTechnology] = useState('')
    const [activeAnalysis, setActiveAnalysis] = useState<ActiveAnalysis>({
        appearance: true,
        taste: true,
        texture: true,
        packaging: true
    })
    const [lineProducts, setLineProducts] = useState<{ productName: string; skus: string[] }[]>([])
    const [lineOperators, setLineOperators] = useState<LineOperators[]>([emptyOperatorData()])
    const [deviations, setDeviations] = useState<Deviations>({
        packaging: [],
        appearance: [],
        texture: [],
        taste: []
    })

    useEffect(() => {
        if (line) {
            const countryData = (assetTree as CountryData[]).filter(
                countryData => countryData.countryName === country
            )[0]
            const factoryData = countryData.factories.filter(factoryData => factoryData.factoryName === factory)[0]
            const lineData = factoryData.lines.filter(lineData => lineData.lineName === line)[0]

            setLineProducts(lineData.products)
            setTechnology(lineData.technology || '')
            if (lineData.activeAnalysis) setActiveAnalysis(lineData.activeAnalysis)
            if (lineData.operators.length) setLineOperators(lineData.operators)
            if (lineData.deviations) {
                setDeviations(lineData.deviations)
            }
        }
    }, [assetTree, country, factory, line])

    const lineRequestParams = getLineRequestParams(
        country,
        factory,
        lineOperators,
        lineProducts,
        lineName?.trim(),
        line,
        technology.trim(),
        activeAnalysis,
        deviations
    )

    function handleSubmit(): void {
        setIsLoading(true)
        postOrEditLine(lineRequestParams, line ? 'edit' : 'add').then(postResponse => {
            setIsLoading(false)
            handleResponse(postResponse, 'line')
            updateAssetTreeState(postResponse.status, assetType, setAssetTree)

            if (postResponse.status === 200) window.location.href = '/#/config/' + FACTORY_CONFIG_ID
        })
    }
    const saveDisabledStatus = getLineConfigSaveDisabledStatus(lineRequestParams)

    return (
        <StyledLineConfig>
            <section className="lineConfig--topBar">
                <div className="lineConfig--topBar__container">
                    <a
                        className={'lineConfig--topBar__container--link area-header-back'}
                        href={`#/config/${FACTORY_CONFIG_ID}`}
                    >
                        <i className="icon-area-header-back"></i>
                        {i18n('go back to factories')}
                    </a>
                </div>
            </section>
            <section className="lineConfig--content">
                <div className="lineConfig--content__name-technology">
                    <div className="lineConfig--content__name-technology--name">
                        <label htmlFor="lineName">{i18n('line name')}</label>
                        <input
                            type="text"
                            id={'lineName'}
                            value={lineName}
                            onChange={event => setLineName(event.target.value)}
                        />
                    </div>
                    <div className="lineConfig--content__name-technology--technology">
                        <label htmlFor="technology">{i18n('Technology')}</label>
                        <input
                            type="text"
                            id={'technology'}
                            value={technology}
                            onChange={event => setTechnology(event.target.value)}
                        />
                    </div>
                </div>
                <div className="lineConfig--content__products">
                    <h3 className="lineConfig--content__activeAnalysis">{i18n('products')}</h3>
                    <LineProductSelect lineProducts={lineProducts} setLineProducts={setLineProducts} />
                </div>
                <div className="lineConfig--content__activeAnalysis">
                    <h3 className="lineConfig--content__activeAnalysis">{i18n('Analysis Steps')}</h3>
                </div>

                {RATING_TYPES.map(rating => {
                    return (
                        <DeviationsTable
                            key={rating}
                            activeAnalysis={activeAnalysis}
                            setActiveAnalysis={setActiveAnalysis}
                            deviations={deviations}
                            rating={rating}
                            setDeviations={setDeviations}
                        />
                    )
                })}

                <div className="lineConfig--content__users">
                    <h3>{i18n('operators')}</h3>
                    <UserTable lineOperators={lineOperators} setLineOperators={setLineOperators} />
                </div>
            </section>
            <div className="footer-Margin" />
            <footer className="lineConfig--footer">
                <LiveTooltip
                    position="top"
                    content={i18n('Please make sure all fields are filled correctly')}
                    hide={!saveDisabledStatus}
                >
                    <div>
                        <SubmitOrCancel
                            onCancel={() => (window.location.href = '/#/config/' + FACTORY_CONFIG_ID)}
                            onSubmit={handleSubmit}
                            saving={isLoading}
                            disabled={saveDisabledStatus}
                        />
                    </div>
                </LiveTooltip>
            </footer>
        </StyledLineConfig>
    )
}

const StyledLineConfig = styled.main`
    display: flex;
    flex-direction: column;
    align-items: center;
    .lineConfig {
        &--topBar__container,
        &--content {
            width: 970px;
            @media (min-width: 1600px) {
                width: 1170px;
            }
        }
        &--topBar {
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${({ theme }) => theme['gray-200']};
            width: 100%;
            &__container {
                display: flex;
                padding: 16px 15px;
                &--link {
                    color: ${({ theme }) => theme['gray-800']};
                    font-size: 20px;
                    text-transform: capitalize;
                    display: flex;
                    align-items: center;
                    text-decoration: none;
                    margin: 0;
                    i {
                        margin: 0;
                        margin-right: 8px;
                    }
                }
            }
        }
        &--content {
            --border: 1px solid ${({ theme }) => theme['gray-400']};
            display: column;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: auto 55px auto;
            gap: 24px 24px;
            grid-template-areas:
                'name technology'
                'products activeAnalysis'
                'products users';
            margin-top: ${liveHeaderHeight + 'px'};
            align-items: start;
            label,
            h3 {
                text-transform: capitalize;
                color: ${({ theme }) => theme['gray-800']};
                font-size: 14px;
                font-family: var(--font-semibold);
            }
            &__name-technology {
                display: grid;
                grid-template-areas: 'name technology';
                align-items: center;
                margin-bottom: 16px;
                &--name {
                    grid-area: name;
                    width: 99%;
                }
                &--technology {
                    grid-area: technology;
                    width: 100%;
                }
            }

            &__products {
                grid-area: products;
            }
            &__users {
                grid-area: users;
                h3 {
                    text-transform: capitalize;
                    color: ${({ theme }) => theme['gray-800']};
                    font-size: 14px;
                    font-family: var(--font-semibold);
                    margin-bottom: 4px;
                }
            }
            &__activeAnalysis {
                grid-area: activeAnalysis;
                display: flex;
                flex-direction: column;
                margin-bottom: 4px;
                &--analysis {
                    display: flex;
                    padding: 8px 0;
                    justify-content: space-between;
                    &__container {
                        display: flex;
                        align-items: center;
                        &--name {
                            font-size: 15px;
                            margin-right: 8px;
                        }
                    }
                }
            }
        }
        &--footer {
            width: 100%;
            align-items: center;
            padding: 8px 24px;
            background: ${({ theme }) => theme['gray-200']};
            bottom: 0;
            left: 0;
            position: fixed;
            margin-top: 60px;
            z-index: 20;
        }
    }
    .footer-Margin {
        height: 90px;
    }
`
